import { useEffect, useState } from "react";

import {
  ArrowTrendingUpIcon,
  BoltIcon,
  ChartBarSquareIcon,
  CurrencyPoundIcon,
  RocketLaunchIcon,
  ShieldCheckIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";

import Card from "../../components/shared/Card/Card";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import classNames from "../../utils/classNames";
import { usePageNameContext } from "../../contexts/PageTitleContext";

// Marketing/preview shell for the upcoming BidPro product — Wilma's
// AI-assisted bidding tool for salvage auctions. There's no backend work
// yet; this page sells the concept, captures interest, and gives the team
// a live URL to link from demos. Once the first models ship we'll swap
// the stat tiles and feature list for real data.

type FeatureTone = "violet" | "emerald" | "blue" | "amber";

interface Feature {
  title: string;
  description: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  tone: FeatureTone;
}

const featureTones: Record<FeatureTone, string> = {
  violet: "bg-violet-50 text-violet-700 ring-violet-200",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  blue: "bg-blue-50 text-blue-700 ring-blue-200",
  amber: "bg-amber-50 text-amber-700 ring-amber-200",
};

const features: Feature[] = [
  {
    title: "Max-bid recommendations",
    description:
      "Wilma models your yard economics against live salvage listings to suggest the highest price you should bid — including fees, transport, and expected dismantling yield.",
    icon: CurrencyPoundIcon,
    tone: "violet",
  },
  {
    title: "Parts yield forecasting",
    description:
      "Predicts the parts mix, expected sale prices, and time-to-sell for each donor before you bid, using your own historical dismantling data.",
    icon: ChartBarSquareIcon,
    tone: "blue",
  },
  {
    title: "Auction house coverage",
    description:
      "Live feeds from Copart, IAA, e2e and the UK's largest salvage auction houses — filter by make, model, damage type and category in one place.",
    icon: BoltIcon,
    tone: "amber",
  },
  {
    title: "Margin guardrails",
    description:
      "Set your minimum margin target and BidPro will cap its recommendations automatically — no more chasing vehicles that won't pay their way.",
    icon: ShieldCheckIcon,
    tone: "emerald",
  },
];

export default function BidPro() {
  const { setInfo } = usePageNameContext();
  const [joined, setJoined] = useState(false);

  useEffect(() => {
    setInfo({
      name: "BidPro",
      desc: "AI-assisted bidding for salvage auctions — early access preview.",
    });
  }, [setInfo]);

  return (
    <div className="mt-4 flex flex-col gap-6">
      {/* Hero — gradient card mirroring the "fancy" accent used on the
          Vehicle Intelligence tab so the beta product feels like a natural
          extension of the existing intelligence surface. */}
      <div className="relative overflow-hidden rounded-lg bg-gradient-to-br from-violet-600 via-fuchsia-500 to-sky-500 text-white shadow-lg">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-white/30 blur-3xl" />
          <div className="absolute -bottom-20 -left-16 h-64 w-64 rounded-full bg-fuchsia-200/30 blur-3xl" />
        </div>

        <div className="relative p-6 sm:p-10 flex flex-col lg:flex-row lg:items-center gap-8">
          <div className="flex-1 min-w-0">
            <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ring-white/40 backdrop-blur-sm">
              <SparklesIcon className="h-3.5 w-3.5" aria-hidden="true" />
              Beta
            </span>
            <h1 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight">
              BidPro
            </h1>
            <p className="mt-2 text-base sm:text-lg text-white/90 max-w-2xl">
              Smart bidding for salvage auctions. Wilma analyses live auction
              listings, your yard's historical parts performance, and current
              market pricing to recommend the maximum price you should bid on
              any vehicle — automatically.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <ButtonInput
                label={joined ? "You're on the list" : "Join the beta"}
                isSubmit={false}
                onClick={() => setJoined(true)}
                disabled={joined}
                classes="!bg-white !text-violet-700 hover:!bg-white/90"
              />
              <a
                href="mailto:support@wilma.ai?subject=BidPro%20Beta%20enquiry"
                className="text-sm font-medium text-white/90 hover:text-white underline-offset-4 hover:underline"
              >
                Talk to the team
              </a>
            </div>
          </div>

          <div className="w-full lg:w-80 shrink-0">
            {/* Mock KPI card — illustrates the "recommended bid vs. projected
                margin" surface we're aiming to ship with v1. Purely static
                for now. */}
            <div className="rounded-lg bg-white/95 text-gray-900 shadow-xl p-5 ring-1 ring-white/20">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Recommended max bid
              </p>
              <p className="mt-1 flex items-baseline gap-2">
                <span className="text-3xl font-bold text-gray-900">£1,850</span>
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
                  <ArrowTrendingUpIcon
                    className="h-3 w-3"
                    aria-hidden="true"
                  />
                  42% margin
                </span>
              </p>
              <div className="mt-4 space-y-2 text-sm">
                <MockRow label="Projected parts revenue" value="£4,120" />
                <MockRow label="Dismantling + storage" value="£540" />
                <MockRow label="Auction fees" value="£180" />
                <MockRow
                  label="Est. net profit"
                  value="£1,550"
                  emphasis
                />
              </div>
              <p className="mt-3 text-[11px] text-gray-400">
                Illustrative — live data coming soon.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Feature grid — the four pillars of the v1 product, in the Card
          grammar used elsewhere in the dashboard so the layout reads as
          "real product" rather than a pitch deck. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {features.map((feature) => {
          const Icon = feature.icon;
          return (
            <Card key={feature.title} bodyClassName="p-5">
              <div className="flex items-start gap-4">
                <span
                  className={classNames(
                    "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset",
                    featureTones[feature.tone]
                  )}
                >
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    {feature.description}
                  </p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Roadmap strip — sets expectations for pilot users about what lands
          when, without over-promising exact dates. */}
      <Card
        title="What's coming"
        bodyClassName="p-6"
        headerRight={
          <RocketLaunchIcon
            className="h-5 w-5 text-violet-500"
            aria-hidden="true"
          />
        }
      >
        <ol className="relative border-l border-gray-200 ml-3">
          <RoadmapItem
            label="Phase 1 · Listings ingestion"
            status="In progress"
            statusTone="violet"
            description="Auction feeds from Copart and the major UK auction houses, normalised into the vehicle model you already see in Wilma."
          />
          <RoadmapItem
            label="Phase 2 · Bid recommendations"
            status="Next up"
            statusTone="blue"
            description="Model your own yard's yield against each listing and surface a recommended max bid with a confidence interval."
          />
          <RoadmapItem
            label="Phase 3 · Auto-bidding"
            status="Planned"
            statusTone="slate"
            description="Opt-in auto-bidding within your configured guardrails, with full audit trail and per-listing overrides."
            isLast
          />
        </ol>
      </Card>
    </div>
  );
}

const MockRow = ({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) => (
  <div
    className={classNames(
      "flex items-center justify-between",
      emphasis
        ? "pt-2 border-t border-gray-100 font-semibold text-gray-900"
        : "text-gray-600"
    )}
  >
    <span>{label}</span>
    <span>{value}</span>
  </div>
);

const ROADMAP_TONES: Record<string, string> = {
  violet: "bg-violet-100 text-violet-700 ring-violet-200",
  blue: "bg-blue-100 text-blue-700 ring-blue-200",
  slate: "bg-slate-100 text-slate-600 ring-slate-200",
};

const RoadmapItem = ({
  label,
  status,
  statusTone,
  description,
  isLast,
}: {
  label: string;
  status: string;
  statusTone: keyof typeof ROADMAP_TONES;
  description: string;
  isLast?: boolean;
}) => (
  <li className={classNames("ml-6", isLast ? "" : "pb-6")}>
    <span
      className={classNames(
        "absolute -left-2 flex h-4 w-4 items-center justify-center rounded-full ring-4 ring-white",
        ROADMAP_TONES[statusTone] ?? ROADMAP_TONES.slate
      )}
    />
    <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1">
      <p className="text-sm font-medium text-gray-900">{label}</p>
      <span
        className={classNames(
          "inline-flex items-center self-start rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
          ROADMAP_TONES[statusTone] ?? ROADMAP_TONES.slate
        )}
      >
        {status}
      </span>
    </div>
    <p className="mt-1 text-sm text-gray-600">{description}</p>
  </li>
);
